import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, setPersistence, browserLocalPersistence, type Auth } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { firebaseConfig } from './firebase.prod';
import type { FirebaseInstances } from '../../types/firebase.types';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let devAuthLoggerAttached = false;
const LOCALHOST_ALLOWED_PROJECTS = new Set(['verdekai-prod', 'verdekai-staging']);

const initializeFirebase = async (): Promise<{ app: FirebaseApp; auth: Auth; db: Firestore; storage: FirebaseStorage }> => {
  if (app && auth && db && storage) {
    return { app, auth, db, storage };
  }

  try {
    if (
      process.env.NODE_ENV === 'development'
      && typeof window !== 'undefined'
      && ['localhost', '127.0.0.1'].includes(window.location.hostname)
      && Boolean(window.location.port)
      && !LOCALHOST_ALLOWED_PROJECTS.has(firebaseConfig.projectId || '')
    ) {
      throw new Error(`Refusing to run localhost against Firebase project ${firebaseConfig.projectId || 'unknown'}`);
    }

    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    auth.useDeviceLanguage();

    // Use local persistence so auth is shared with pop-out windows on the same origin.
    await setPersistence(auth, browserLocalPersistence);

    // Protected workspaces must not persist Firestore documents across browser
    // sessions. Authorization is always re-evaluated by rules/server access;
    // this cache is only a short-lived rendering aid.
    db = initializeFirestore(app, { localCache: memoryLocalCache() });
    storage = getStorage(app);

    if (process.env.NODE_ENV === 'development' && !devAuthLoggerAttached) {
      devAuthLoggerAttached = true;
      console.info('[Firebase] initialized', {
        projectId: firebaseConfig.projectId,
        authDomain: firebaseConfig.authDomain,
      });
      onAuthStateChanged(auth, (user) => {
        console.info('[Firebase] auth state', {
          projectId: firebaseConfig.projectId,
          uid: user?.uid || null,
          signedIn: Boolean(user),
        });
      });
    }

    return { app, auth, db, storage };
  } catch (error) {
    throw error;
  }
}

export const getFirebaseInstances = async (): Promise<FirebaseInstances> => {
  const instances = await initializeFirebase();
  // Return with firestore alias for backwards compatibility
  return {
    ...instances,
    firestore: instances.db
  };
};

// Initialize Firebase immediately
getFirebaseInstances().catch(() => {});
